import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import MerchantAuthLayout from "./MerchantAuthLayout";
import { useMerchantAuth } from "../../context/MerchantAuthContext";
import merchantApi from "../../lib/merchantApi";

export default function Login() {
  const { user, loading, login } = useMerchantAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/app";
  const reason = new URLSearchParams(location.search).get("reason");

  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate(redirectTo, { replace: true });
  }, [user, loading, navigate, redirectTo]);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not sign you in. Please try again.");
      setSubmitting(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await merchantApi.post("/merchant/forgot-password", { email: email.trim().toLowerCase() });
      setForgotSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not send the reset link. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === "forgot") {
    return (
      <MerchantAuthLayout
        eyebrow="Reset password"
        title={forgotSent ? "Check your inbox" : "Forgot your password?"}
        subtitle={
          forgotSent
            ? "If that email is registered with Neptune, you'll receive a reset link within a minute. The link expires in 24 hours."
            : "Enter your account email and we'll send you a secure link to set a new password."
        }
        testId="merchant-forgot-page"
      >
        {!forgotSent ? (
          <form onSubmit={onForgot} className="space-y-5" data-testid="forgot-form">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Work email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                required
                data-testid="forgot-email-input"
                className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 transition-all px-4 py-3 text-[14px] text-white placeholder:text-zinc-600"
              />
            </div>

            {error && (
              <div data-testid="forgot-error" className="rounded-xl border border-rose-400/20 bg-rose-400/[0.05] text-rose-200 text-[12.5px] px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!email.trim() || submitting}
              data-testid="forgot-submit-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all w-full px-5 py-3.5 text-[14px] font-medium"
            >
              {submitting ? "Sending…" : "Send reset link"}
              {!submitting && <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />}
            </button>
          </form>
        ) : (
          <div className="text-center" data-testid="forgot-sent-confirm">
            <div className="text-[13px] text-zinc-300 leading-relaxed">
              We sent a reset link to <span className="text-white">{email}</span>.
            </div>
            <div className="mt-4 text-[11.5px] text-zinc-500 leading-relaxed">
              Didn&apos;t get it within 2 minutes? Check your spam folder, or email{" "}
              <a
                href="mailto:support@neptunepay.io?subject=Password%20reset%20help"
                data-testid="forgot-support-link"
                className="text-zinc-300 hover:text-[#00E5FF] underline underline-offset-2"
              >
                support@neptunepay.io
              </a>{" "}
              for help.
            </div>
          </div>
        )}

        <div className="mt-7 pt-6 border-t border-white/[0.06] text-center text-[13px] text-zinc-400">
          <button
            type="button"
            onClick={() => { setMode("login"); setForgotSent(false); setError(""); }}
            data-testid="forgot-back-to-login"
            className="text-white hover:text-[#00E5FF] transition-colors font-medium"
          >
            ← Back to login
          </button>
          {/* Brief 3.13.2 (Feb 27 2026) — admins kept landing here by
             mistake. Their accounts use Google SSO and have no merchant
             password to reset, so the form silently 200s and they never
             get an email. Surface the right path here. */}
          <div className="mt-4 text-[11.5px] text-zinc-500 leading-relaxed"
               data-testid="forgot-admin-hint">
            Looking to sign in to the Neptune back office?{" "}
            <a href="/admin/login"
               data-testid="forgot-admin-signin-link"
               className="text-zinc-300 hover:text-[#00E5FF] underline underline-offset-2">
              Admins sign in with Google here →
            </a>
          </div>
        </div>
      </MerchantAuthLayout>
    );
  }

  return (
    <MerchantAuthLayout
      eyebrow="Sign in"
      title="Welcome back"
      subtitle="Sign in to your Neptune merchant account."
      testId="merchant-login-page"
    >
      <form onSubmit={onLogin} className="space-y-5" data-testid="merchant-login-form">
        {reason === "logout-all" && (
          <div
            data-testid="login-logout-all-banner"
            className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.06] px-4 py-3 text-emerald-100 text-[12.5px] leading-relaxed"
          >
            You've been signed out of all devices. Sign in again to continue.
          </div>
        )}
        {reason === "password-changed" && (
          <div
            data-testid="login-password-changed-banner"
            className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.06] px-4 py-3 text-emerald-100 text-[12.5px] leading-relaxed"
          >
            Your password has been changed. Please sign in with your new password.
          </div>
        )}
        {reason === "check-email" && (
          <div
            data-testid="login-check-email-banner"
            className="rounded-xl border border-[#00E5FF]/20 bg-[#00E5FF]/[0.06] px-4 py-3 text-cyan-100 text-[12.5px] leading-relaxed"
          >
            Check your email — we&rsquo;ve sent you a message about your Neptune account.
          </div>
        )}
        <div>
          <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Work email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            required
            data-testid="login-email-input"
            className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 transition-all px-4 py-3 text-[14px] text-white placeholder:text-zinc-600"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Password</label>
            <button
              type="button"
              onClick={() => { setMode("forgot"); setError(""); }}
              data-testid="login-forgot-link"
              className="text-[11.5px] text-zinc-400 hover:text-[#00E5FF] transition-colors"
            >
              Forgot?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              autoComplete="current-password"
              required
              data-testid="login-password-input"
              className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 transition-all px-4 py-3 pr-11 text-[14px] text-white placeholder:text-zinc-600"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              data-testid="login-toggle-password"
              aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {error && (
          <div data-testid="login-error" className="rounded-xl border border-rose-400/20 bg-rose-400/[0.05] text-rose-200 text-[12.5px] px-4 py-3">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!email.trim() || !password || submitting}
          data-testid="login-submit-btn"
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all w-full px-5 py-3.5 text-[14px] font-medium"
        >
          {submitting ? "Signing in…" : "Sign in"}
          {!submitting && <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />}
        </button>
      </form>

      <div className="mt-7 pt-6 border-t border-white/[0.06] text-center text-[13px] text-zinc-400">
        New to Neptune?{" "}
        <Link
          to="/signup"
          data-testid="login-go-to-signup"
          className="text-white hover:text-[#00E5FF] transition-colors font-medium"
        >
          Create an account
        </Link>
      </div>
    </MerchantAuthLayout>
  );
}
